import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  CreatableOptionPicker,
  EntitySelectorFooterAction,
  type CreatableOption,
} from "@salestrak/ui";

import { VocabularyFieldTrigger } from "#app/components/follow-ups/vocabulary-field-trigger";
import { OutcomeFormDialog } from "#app/components/settings/outcome-form-dialog";
import { useReasonMutations } from "#app/hooks/settings/use-reason-mutations";
import type { ReasonRow } from "#app/lib/data";
import { useWorkspace } from "#app/lib/data/workspace-context";
import { usePermission } from "#app/lib/hooks/use-permission";
import { normalizeOutcomeTone } from "#app/lib/outcome-tone";

/** Hoisted so the empty fixed slot keeps one identity across renders — the
 *  picker memos over `options`, and a fresh `[]` inline would re-run them. */
const NO_FIXED_OPTIONS: CreatableOption[] = [];

/**
 * What came of a Project Update — the server outcome vocabulary ("reasons" in
 * the API, outcomes everywhere on screen), on the picker the follow-up
 * dialogs' Outcome question uses, so the two posting dialogs read as one
 * control. Same governance grammar as the follow-up pools: no Create here
 * (an outcome carries a tone the settings page sections by), "…" Edit and
 * Delete for a holder of `reasons.manage`, a footer to the page, and pick for
 * everyone else.
 *
 * The host hands in the rows it already queried — this dialog's query and
 * the pool are one fetch, not two.
 */
export function ProjectOutcomePicker({
  reasons,
  value,
  onValueChange,
  required = false,
}: {
  /** Non-archived reasons, as the Update dialog's query returns them. */
  reasons: ReasonRow[];
  value: string | null;
  onValueChange: (reasonId: string | null) => void;
  /** Require outcome is on — no clear row; Save gating is the dialog's job. */
  required?: boolean;
}) {
  const { activeWorkspace } = useWorkspace();
  const canManage = usePermission("reasons.manage");
  const { updateReason, deleteReason } = useReasonMutations();
  const navigate = useNavigate();
  const [editing, setEditing] = useState<ReasonRow | null>(null);

  const options = useMemo(
    () =>
      reasons.map(
        (reason): CreatableOption => ({ id: reason.reasonId, label: reason.name }),
      ),
    [reasons],
  );
  const selected = useMemo(
    () => reasons.find((reason) => reason.reasonId === value) ?? null,
    [reasons, value],
  );

  const handleEdit = useCallback(
    (option: CreatableOption) => {
      setEditing(reasons.find((reason) => reason.reasonId === option.id) ?? null);
    },
    [reasons],
  );
  const handleDelete = useCallback(
    (reasonId: string) => {
      const reason = reasons.find((row) => row.reasonId === reasonId);
      if (!reason) return;
      void deleteReason(reason);
      if (value === reasonId) onValueChange(null);
    },
    [deleteReason, onValueChange, reasons, value],
  );
  const handleClear = useCallback(() => onValueChange(null), [onValueChange]);

  return (
    <>
      <CreatableOptionPicker
        options={NO_FIXED_OPTIONS}
        manageableOptions={options}
        selectedId={selected?.reasonId ?? null}
        onPick={(option) => onValueChange(option.id)}
        onClear={required ? undefined : handleClear}
        onEdit={canManage ? handleEdit : undefined}
        onDelete={canManage ? handleDelete : undefined}
        noun="Outcome"
        searchPlaceholder="Search outcomes..."
        fit
        footer={
          canManage ? (
            <EntitySelectorFooterAction
              icon="settings-2"
              onClick={() => {
                if (!activeWorkspace) return;
                navigate({
                  to: "/$workspace/settings/outcomes",
                  params: { workspace: activeWorkspace.urlPath },
                  search: { tab: "projects" },
                });
              }}
            >
              Manage outcomes
            </EntitySelectorFooterAction>
          ) : undefined
        }
      >
        <VocabularyFieldTrigger
          ariaLabel="Outcome"
          chevron="chevrons-up-down"
          placeholder={required ? "Select an outcome" : "No outcome"}
          value={selected?.name ?? ""}
        />
      </CreatableOptionPicker>

      <OutcomeFormDialog
        open={!!editing}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
        initialName={editing?.name}
        initialTone={editing ? normalizeOutcomeTone(editing.tone) : undefined}
        onSubmit={async ({ name, tone }) => {
          if (editing) await updateReason(editing.id, name, tone);
        }}
      />
    </>
  );
}
